'use client'

import { useCallback, useEffect, useState } from 'react'

import {
  COOKIE_DAYS,
  COOKIE_NAME,
  parseHistory,
  removeFromHistory,
  serializeHistory,
} from '@/app/lib/recentlyViewed'
import { deleteCookie, getCookie, setCookie } from '@/app/lib/utils'

interface UseRecentlyViewedHistory {
  /** `null` mientras no se ha leído la cookie (servidor y primer render de
   * cliente coinciden, sin *hydration mismatch*); array una vez resuelta. */
  readonly ids: readonly string[] | null
  /** Da de baja un evento; elimina la cookie si el historial queda vacío. */
  readonly remove: (id: string) => void
  /** Vacía el historial eliminando la cookie. */
  readonly clear: () => void
}

/**
 * Puente entre la cookie del historial y el estado de React. Toda la lógica
 * (formato, orden, deduplicación, tope) vive en `lib/recentlyViewed`; aquí solo
 * se lee/escribe `document.cookie` y se mantiene el estado.
 *
 * Deliberadamente NO se resuelve con `cookies()` de servidor: forzaría *dynamic
 * rendering* en las páginas que alojan el bloque (mismo criterio y precedente
 * que `useAppPromotionCookie`, ver specs/024-vistos-recientemente/research §1).
 */
export function useRecentlyViewedHistory(): UseRecentlyViewedHistory {
  const [ids, setIds] = useState<readonly string[] | null>(null)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- lectura de cookie post-hidratación, ver architecture.rule.md
    setIds(parseHistory(getCookie(COOKIE_NAME)))
  }, [])

  /** Persiste el historial: escribe con vigencia renovada, o elimina la cookie
   * cuando queda vacío (FR-019 — no se deja una cookie con valor vacío). */
  const persist = useCallback((next: readonly string[]) => {
    if (next.length === 0) {
      deleteCookie(COOKIE_NAME)
    } else {
      setCookie(COOKIE_NAME, serializeHistory(next), COOKIE_DAYS)
    }
  }, [])

  const remove = useCallback(
    (id: string) => {
      setIds((current) => {
        // `serializeHistory` recorta al tope, así que una cookie sembrada desde
        // fuera con más de 15 ids queda saneada en esta misma escritura (FR-036).
        const next = removeFromHistory(current ?? [], id)
        persist(next)
        return next
      })
    },
    [persist],
  )

  const clear = useCallback(() => {
    deleteCookie(COOKIE_NAME)
    setIds([])
  }, [])

  return { ids, remove, clear }
}
